<template>
  <div class="coupon-page">
    <div class="tabs-container">
      <div class="tab-item" v-for="(name, index) in ['待使用', '已使用', '已过期']" :key="index"
        :class="{ active: activeTab === index + 1 }" @click="switchTab(index + 1)">
        {{ name }}
        <div class="active-line" v-if="activeTab === index + 1"></div>
      </div>
    </div>

    <div class="coupon-list" ref="scrollContainer" @scroll="handleScroll">
      <div v-if="!loading && list.length === 0" class="empty-tip">
        <img class="package-empty-icon" src="@/assets/empty-order.png" />
        <p class="package-empty-text">暂无优惠券</p>
      </div>

      <div v-for="item in list" :key="item.id" class="coupon-card" :class="{
        'card-disabled': activeTab !== 1,
        'has-footer': item.rules && item.rules.length > 0,
      }" :style="getCardStyle(item)">
        <div class="card-main" @click="handleCardClick(item)">
          <div class="left-part">
            <div class="price">
              <span class="symbol" v-if="item.discountType !== 3">¥</span>
              <span class="num">{{ item.displayAmount }}</span>
              <span class="symbol" v-if="item.discountType === 3">折</span>
            </div>
            <div class="condition">{{ item.displayCondition }}</div>
          </div>

          <div class="divider-line"></div>

          <div class="right-part">
            <div class="info">
              <div class="name">{{ item.couponName }}</div>
              <div class="date">有效期至 {{ item.expireTime }}</div>
            </div>

            <div class="status-area">
              <div v-if="activeTab === 1" class="use-btn" @click.stop="handleUse(item)">
                去使用
              </div>

              <div v-else class="status-badge" :style="{ backgroundColor: 'var(--badge-bg)', color: '#fff' }">
                {{ getStatusText(activeTab) }}
              </div>
            </div>
          </div>
        </div>

        <div class="card-footer" v-if="item.rules && item.rules.length > 0">
          <div class="rules-content" :class="{ expanded: item.isExpanded }">
            <div v-for="(rule, rIndex) in item.rules" :key="rIndex" class="rule-item">
              {{ rIndex + 1 }}、{{ rule }}
            </div>
          </div>

          <div class="toggle-btn" @click.stop="toggleExpand(item)">
            <img class="arrow-img" :src="item.isExpanded ? arrowUpIcon : arrowDownIcon" alt="toggle" />
          </div>
        </div>
      </div>

      <div class="loading-more" v-if="list.length > 0">
        {{ finished ? "没有更多了" : "加载中..." }}
      </div>
    </div>
  </div>
</template>

<script>
import { getCouponList } from "@/api/user";

export default {
  data() {
    return {
      // 核心状态：1=待使用, 2=已使用, 3=已过期
      activeTab: 1,

      // 数据源
      list: [],

      // 分页控制
      loading: false,
      finished: false,
      pageIndex: 1,
      pageSize: 20,

      // UI 配置
      primaryColor: "#FD8D32", // 默认兜底色
      arrowDownIcon: require("@/assets/2026/2026down.png"),
      arrowUpIcon: require("@/assets/2026/2026up.png"),
    };
  },

  mounted() {
    this.initThemeColor();
    this.getListAction(true); // 首次加载
  },

  methods: {
    // 1. 初始化主题色
    initThemeColor() {
      try {
        const styles = getComputedStyle(document.documentElement);
        const color = styles.getPropertyValue("--primary-color").trim();
        if (color) this.primaryColor = color;
      } catch (e) {
        console.warn("Theme init error", e);
      }
    },

    // 2. 核心逻辑：后端数据转前端展示
    formatCouponItem(item) {
      let displayAmount = "0";
      let displayCondition = "-";

      // 处理金额/门槛显示逻辑
      // discountType: 1=立减, 2=满减, 3=折扣
      switch (item.discountType) {
        case 1: // 立减
          displayAmount = item.fixedAmount;
          displayCondition = "立减";
          break;
        case 2: // 满减
          displayAmount = item.fullReduceAmount;
          displayCondition = `满${item.fullReduceThreshold}可用`;
          break;
        case 3: // 折扣
          displayAmount = item.discountValue;
          displayCondition = `满${item.discountThreshold}可用`;
          break;
        default:

      }

      return {
        ...item,
        displayAmount,
        displayCondition,
        rules: item.usageLimits,
        isExpanded: false, // 默认收起
        expireTime: item.expireTime ? `${item.expireTime.replace(/-/g, ".")}` : "",
      };
    },

    // 3. 颜色转换工具
    convertColorToRgba(colorStr, opacity) {
      if (!colorStr) return `rgba(253, 141, 50, ${opacity})`;
      const color = colorStr.trim();
      if (color.startsWith("#")) {
        const hex = color.replace("#", "");
        let r, g, b;
        if (hex.length === 3) {
          r = parseInt(hex[0] + hex[0], 16);
          g = parseInt(hex[1] + hex[1], 16);
          b = parseInt(hex[2] + hex[2], 16);
        } else {
          r = parseInt(hex.substring(0, 2), 16);
          g = parseInt(hex.substring(2, 4), 16);
          b = parseInt(hex.substring(4, 6), 16);
        }
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }
      return color;
    },

    // 4. 动态计算卡片样式
    getCardStyle(item) {
      // 如果不是 "待使用" (1)，则置灰
      if (this.activeTab !== 1) {
        const fadedColor = this.convertColorToRgba(this.primaryColor, 0.6);
        return {
          "--card-bg": "#fff",
          "--main-text": "#999",
          "--theme-color": fadedColor,
          "--divider-color": this.convertColorToRgba(this.primaryColor, 0.3),
          "--badge-bg": "#FFCD99",
        };
      }
      // 正常状态
      return {
        "--card-bg": this.convertColorToRgba(this.primaryColor, 0.2),
        "--main-text": "#24262B",
        "--theme-color": this.primaryColor,
        "--divider-color": this.convertColorToRgba(this.primaryColor, 0.6),
        "--badge-bg": this.primaryColor,
      };
    },

    // 5. 切换 Tab
    switchTab(tabValue) {
      if (this.activeTab === tabValue) return;
      this.activeTab = tabValue;

      // 切换时滚动条回顶
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollTop = 0;
      }

      this.getListAction(true); // 刷新列表
    },

    getStatusText(tabValue) {
      const map = { 2: "已使用", 3: "已过期" };
      return map[tabValue] || "";
    },

    toggleExpand(item) {
      // 展开/收起规则
      this.$set(item, "isExpanded", !item.isExpanded);
    },

    handleUse(item) {
      // 跳转逻辑
      this.uni.navigateTo("/home/updateBuy?isShowOther=1&isenterAuth=0");
    },

    handleCardClick(item) {
      console.log("卡片详情", item);
    },

    // 6. 滚动加载监听
    handleScroll(e) {
      const target = e.target;
      // 触底 50px 时加载下一页
      if (target.scrollHeight - target.scrollTop - target.clientHeight < 50) {
        this.getListAction();
      }
    },

    // 7. API 请求
    getListAction(isRefresh = false) {
      if (this.loading) return;
      if (!isRefresh && this.finished) return;

      this.loading = true;

      if (isRefresh) {
        this.pageIndex = 1;
        this.list = [];
        this.finished = false;
      }

      const params = {
        index: this.pageIndex,
        pageSize: this.pageSize,
        couponStatus: this.activeTab, // 直接透传 1, 2, 3
      };

      getCouponList(params)
        .then((res) => {
          const rawList = res.list || [];
          const formattedList = rawList.map((item) =>
            this.formatCouponItem(item)
          );

          if (isRefresh) {
            this.list = formattedList;
          } else {
            this.list = [...this.list, ...formattedList];
          }

          // 判断是否还有数据
          if (formattedList.length < this.pageSize) {
            this.finished = true;
          } else {
            this.pageIndex++;
          }

        }).catch((err) => {
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="less" scoped>
.coupon-page {
  height: 100vh;
  background-color: #f2f2f2;
  display: flex;
  flex-direction: column;
}

.tabs-container {
  display: flex;
  background: #fff;
  height: 92px;
  align-items: center;
  justify-content: space-around;
  flex-shrink: 0;
  z-index: 10;

  .tab-item {
    position: relative;
    font-size: 30px;
    color: #999;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    cursor: pointer;

    &.active {
      color: #333;
      font-weight: 500;
    }

    .active-line {
      position: absolute;
      bottom: 10px;
      width: 48px;
      height: 8px;
      background-color: var(--primary-color);
      border-radius: 8px;
    }
  }
}

.coupon-list {
  padding: 24px 32px;
  flex: 1;
  overflow-y: auto;
  /* 关键：开启滚动 */
  -webkit-overflow-scrolling: touch;

  .empty-tip {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .package-empty-icon {
      margin-top: 120px;
      width: 342px;
      height: 264px;
    }

    .package-empty-text {
      font-size: 30px;
      color: #666666;
      margin-top: 72px;
    }
  }

  .loading-more {
    text-align: center;
    padding: 20px 0;
    color: #999;
    font-size: 24px;
  }
}

.coupon-card {
  margin-bottom: 24px;
  border-radius: 16px;
  overflow: hidden;

  /* 有 footer 时，隐藏中间分割线下部的圆点 */
  &.has-footer {
    .card-main .divider-line::after {
      display: none;
    }
  }

  .card-main {
    background-color: var(--card-bg);
    display: flex;
    align-items: stretch;
    height: 188px;
    position: relative;
    transition: background-color 0.3s;

    .left-part {
      width: 175px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: var(--theme-color);
      flex-shrink: 0;

      .price {
        font-weight: bold;
        display: flex;
        align-items: baseline;

        .symbol {
          font-size: 32px;
          margin: 0 4px;
        }

        .num {
          font-size: 56px;
          line-height: 1;
        }
      }

      .condition {
        font-size: 22px;
        margin-top: 10px;
        opacity: 0.8;
        text-align: center;
        padding: 0 5px;
      }
    }

    .divider-line {
      width: 1px;
      margin: 0 12px;
      border-left: 2px dashed var(--divider-color);
      position: relative;

      &::before,
      &::after {
        content: "";
        position: absolute;
        width: 32px;
        height: 32px;
        background-color: #f2f2f2;
        border-radius: 50%;
        left: 50%;
        transform: translateX(-50%);
        z-index: 1;
      }

      &::before {
        top: -16px;
      }

      &::after {
        bottom: -16px;
      }
    }

    .right-part {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 20px 0 10px;
      min-width: 0;

      .info {
        display: flex;
        flex-direction: column;
        justify-content: center;
        flex: 1;
        overflow: hidden;

        .name {
          font-size: 32px;
          font-weight: bold;
          color: var(--main-text);
          margin-bottom: 20px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .date {
          font-size: 22px;
          color: var(--theme-color);
          white-space: nowrap;
        }
      }

      .status-area {
        flex-shrink: 0;
        margin-left: 10px;

        .use-btn {
          background-color: var(--primary-color);
          color: #fff;
          font-size: 24px;
          padding: 10px 24px;
          border-radius: 30px;
          cursor: pointer;
        }

        .status-badge {
          font-size: 24px;
          padding: 10px 24px;
          border-radius: 30px;
        }
      }
    }
  }

  .card-footer {
    background-color: #fff;
    padding: 20px 30px;
    position: relative;

    .rules-content {
      font-size: 24px;
      color: #999;
      line-height: 1.6;
      max-height: 40px;
      /* 默认只显示一行 */
      overflow: hidden;
      padding-right: 40px;
      transition: max-height 0.3s ease;

      &.expanded {
        max-height: 500px;
      }
    }

    .toggle-btn {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: flex-start;
      justify-content: center;

      .arrow-img {
        width: 32px;
        height: 32px;
        object-fit: contain;
      }
    }
  }
}
</style>
